import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { type RouteConfig, useRouteConfig } from '@/context/route-config';
import { useIdentity } from '@/features/onboarding/personal-information/hooks/use-identity';
import { useIsPeriodicReview } from '@/features/periodic-review/context';
import { useProfileFcaQuestionnaireEligibility, useProfileFcaQuestionnaireMutation } from '@/hooks/queries/use-profile-fca-questionnaire';
import { type FcaQuestionnaireAnswers, fcaQuestionnaireAnswersSchema } from '@/services/api/profile';
import { AccountType } from '@/utils/account-type';

export interface FcaQuestionnaireRouteConfig extends RouteConfig {
  ineligible: string;
  next: string;
  personalInformation: string;
}

const FcaQuestionnaireFormForm = () => {
  const form = useForm<FcaQuestionnaireAnswers>({
    defaultValues: {
      selfCertification: undefined,
    },
    resolver: standardSchemaResolver(fcaQuestionnaireAnswersSchema),
  });
  const { isPending, mutate } = useProfileFcaQuestionnaireMutation();
  const routeConfig = useRouteConfig<FcaQuestionnaireRouteConfig>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = async (data: FcaQuestionnaireAnswers) => {
    mutate(
      { answers: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          if (data.selfCertification === 'none') {
            navigate(routeConfig.ineligible, { replace: true });
          } else {
            navigate(routeConfig.next, { replace: true });
          }
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form name="fca-questionnaire" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            name="selfCertification"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <RadioGroup defaultValue={field.value} variant="cards" onValueChange={field.onChange}>
                  <FormItem>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value="authorizedPerson" />
                      </FormControl>
                      <FormLabel contained>{t('FCAQuestionnaire.form.options.1.text')}</FormLabel>
                      <FormDescription className="text-muted-foreground peer-aria-[checked=true]:text-primary">
                        <Trans
                          i18nKey="FCAQuestionnaire.form.options.1.info.text"
                          values={{
                            linkText: t('FCAQuestionnaire.form.options.1.info.linkText'),
                          }}
                          components={[
                            <a
                              key="FCAQuestionnaire.form.options.1.info.linkHref"
                              href={t('FCAQuestionnaire.form.options.1.info.linkHref')}
                              target="_blank"
                              className="relative underline hover:no-underline"
                              rel="external noopener noreferrer"
                            >
                              {t('FCAQuestionnaire.form.options.1.info.linkText')}
                            </a>,
                          ]}
                        />
                      </FormDescription>
                    </RadioGroupItemCard>
                  </FormItem>
                  <FormItem>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value="exemptPerson" />
                      </FormControl>
                      <FormLabel contained>{t('FCAQuestionnaire.form.options.2.text')}</FormLabel>
                      <FormDescription className="text-muted-foreground peer-aria-[checked=true]:text-primary">
                        <Trans
                          i18nKey="FCAQuestionnaire.form.options.2.info.text"
                          values={{
                            linkText: t('FCAQuestionnaire.form.options.2.info.linkText'),
                            linkText1: t('FCAQuestionnaire.form.options.2.info.linkText1'),
                          }}
                          components={[
                            <a
                              key="FCAQuestionnaire.form.options.2.info.linkHref"
                              href={t('FCAQuestionnaire.form.options.2.info.linkHref')}
                              target="_blank"
                              className="relative underline hover:no-underline"
                              rel="external noopener noreferrer"
                            >
                              {t('FCAQuestionnaire.form.options.2.info.linkText')}
                            </a>,
                            <a
                              key="FCAQuestionnaire.form.options.2.info.linkHref1"
                              href={t('FCAQuestionnaire.form.options.2.info.linkHref1')}
                              target="_blank"
                              className="relative underline hover:no-underline"
                              rel="external noopener noreferrer"
                            >
                              {t('FCAQuestionnaire.form.options.2.info.linkText1')}
                            </a>,
                          ]}
                        />
                      </FormDescription>
                    </RadioGroupItemCard>
                  </FormItem>
                  <FormItem>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value="ordinaryBusinessPerson" />
                      </FormControl>
                      <FormLabel contained>{t('FCAQuestionnaire.form.options.3.text')}</FormLabel>
                      <FormDescription className="text-muted-foreground peer-aria-[checked=true]:text-primary">
                        <Trans
                          i18nKey="FCAQuestionnaire.form.options.3.info.text"
                          values={{
                            linkText: t('FCAQuestionnaire.form.options.3.info.linkText'),
                            linkText1: t('FCAQuestionnaire.form.options.3.info.linkText1'),
                          }}
                          components={[
                            <a
                              key="FCAQuestionnaire.form.options.3.info.linkHref"
                              href={t('FCAQuestionnaire.form.options.3.info.linkHref')}
                              target="_blank"
                              className="relative underline hover:no-underline"
                              rel="external noopener noreferrer"
                            >
                              {t('FCAQuestionnaire.form.options.3.info.linkText')}
                            </a>,
                            <a
                              className="relative underline hover:no-underline"
                              href={t('FCAQuestionnaire.form.options.3.info.linkHref1')}
                              key="FCAQuestionnaire.form.options.3.info.linkHref1"
                              rel="external noopener noreferrer"
                              target="_blank"
                            >
                              {t('FCAQuestionnaire.form.options.3.info.linkText1')}
                            </a>,
                          ]}
                        />
                      </FormDescription>
                    </RadioGroupItemCard>
                  </FormItem>
                  <p className="text-xs text-muted-foreground" role="alert" aria-live="polite">
                    <Trans
                      i18nKey="FCAQuestionnaire.form.attestNote.text"
                      values={{
                        linkText: t('FCAQuestionnaire.form.attestNote.linkText'),
                      }}
                      components={[
                        <a
                          key="FCAQuestionnaire.form.attestNote.linkHref"
                          href={t('FCAQuestionnaire.form.attestNote.linkHref')}
                          target="_blank"
                          className="relative underline hover:no-underline"
                          rel="external noopener noreferrer"
                        >
                          {t('FCAQuestionnaire.form.attestNote.linkText')}
                        </a>,
                      ]}
                    />
                  </p>
                  <FormItem>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value="none" />
                      </FormControl>
                      <FormLabel contained>{t('FCAQuestionnaire.form.options.4.text')}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
FcaQuestionnaireFormForm.displayName = 'FcaQuestionnaireFormForm';

const FcaQuestionnaireForm = () => {
  const isPeriodicReview = useIsPeriodicReview();
  const { isComplete: isCompleteIdentity, isLoading: isLoadingIdentity, data: identity } = useIdentity();
  const {
    isLoading: isLoadingEligibility,
    data: isEligible,
    isRequired,
  } = useProfileFcaQuestionnaireEligibility(identity?.addressOfResidency.country, isPeriodicReview ? AccountType.SPOT : undefined);
  const routeConfig = useRouteConfig<FcaQuestionnaireRouteConfig>();
  const { t } = useTranslation();
  const isLoading = isLoadingIdentity || isLoadingEligibility;

  if (isLoading) return <LoadingCard />;

  if (!isCompleteIdentity) return <Navigate to={routeConfig.personalInformation} replace />;

  if (!isRequired || isEligible) return <Navigate to={routeConfig.next} replace />;

  // Only support's retake link, which uses this form outside a review, offers overwriting a "none"
  if (isPeriodicReview && isEligible === false) return <Navigate to={routeConfig.ineligible} replace />;

  return (
    <>
      <PageHeading>
        <PageHeadingTitle>{t('FCAQuestionnaire.form.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FCAQuestionnaire.form.description')}</PageHeadingDescription>
      </PageHeading>
      <PageCard size="lg" bordered fixedFooter>
        <FcaQuestionnaireFormForm />
      </PageCard>
    </>
  );
};
FcaQuestionnaireForm.displayName = 'FcaQuestionnaireForm';

export default FcaQuestionnaireForm;
